import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { Subject } from 'rxjs';
import { take, takeUntil, filter } from 'rxjs/operators';
import { PerfectScrollbarDirective } from 'ngx-perfect-scrollbar';
import * as Feather from 'feather-icons';

import { CoreConfigService } from '@core/services/config.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';

@Component({
  selector: 'vertical-menu',
  templateUrl: './vertical-menu.component.html',
  styleUrls: ['./vertical-menu.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VerticalMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  coreConfig: any;
  menu: any;
  isCollapsed = true;
  isScrolled = false;

  private _unsubscribeAll: Subject<any>;

  constructor(
    private _coreConfigService: CoreConfigService,
    private _coreMenuService: CoreMenuService,
    private _coreSidebarService: CoreSidebarService,
    private _router: Router
  ) {
    this._unsubscribeAll = new Subject();
  }

  @ViewChild(PerfectScrollbarDirective, { static: false }) directiveRef?: PerfectScrollbarDirective;

  ngOnInit(): void {
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.isCollapsed = config.layout.menu.collapsed;
    });

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        if (this._coreSidebarService.getSidebarRegistry('menu')) {
          this._coreSidebarService.getSidebarRegistry('menu').close();
        }
      });

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.directiveRef?.scrollToElement('.navigation .active', -180, 500);
        });
      });

    this._coreMenuService.onMenuChanged
      .pipe(
        filter(value => value !== null),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.menu = this._coreMenuService.getCurrentMenu();
      });
  }

  ngAfterViewInit(): void {
    this.refreshFeatherIcons();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onSidebarScroll(): void {
    if (this.directiveRef?.position(true).y > 3) {
      this.isScrolled = true;
    } else {
      this.isScrolled = false;
    }
  }

  toggleSidebar(): void {
    this._coreSidebarService.getSidebarRegistry('menu').toggleOpen();
  }

  /** Épingler le menu ouvert ou réduit ; survol ouvre temporairement si réduit. */
  toggleMenuPinned(): void {
    const pinOpen = this.isCollapsed;
    this._coreConfigService.setConfig(
      {
        layout: {
          menu: {
            collapsed: !pinOpen,
            hoverExpand: !pinOpen,
          },
        },
      },
      { emitEvent: true }
    );
    this.isCollapsed = !pinOpen;
    setTimeout(() => this.refreshFeatherIcons(), 0);
  }

  get menuPinLabel(): string {
    return this.isCollapsed ? 'Ouvrir le menu' : 'Réduire le menu';
  }

  get menuPinIcon(): string {
    return this.isCollapsed ? 'chevrons-right' : 'chevrons-left';
  }

  private refreshFeatherIcons(): void {
    try {
      Feather.replace();
    } catch {
      /* SSR / tests */
    }
  }
}
